/**
 * Single decision point for "does this merchant see the appointment upsell,
 * and with what numbers".
 *
 * Both consumers go through here so they cannot disagree:
 *   - `getAppSettings`, which hands the result to the micro-apps as a flag
 *   - `BookingsUpgradeContext`, which drives the side-menu card and the dialog
 */
import { WSB_UPGRADE_URL } from '../../constants';
import { upgradeTargetFor, type UpgradeTarget, type WsbPlan } from './plan';
import type { AppointmentUsage } from './usage';

/** What a surface needs to render the upsell. */
export interface UpgradeEligibility extends AppointmentUsage {
  /** The plan the merchant is on. Only the two capped plans reach here. */
  plan: WsbPlan;
  /** The plan being sold to them; decides the copy and the quoted price. */
  upgradeTarget: UpgradeTarget;
  /**
   * Whether appointments are part of the plan at all.
   *
   * `false` only on Portfolio, where there is no allowance to spend and no
   * count to render — a permanent block rather than a threshold that was
   * crossed. Sent explicitly rather than inferred from `limit === 0`, because
   * every other layer reads a limit of zero as "no cap resolved, fail open"
   * (see ./plan.ts).
   */
  appointmentsIncluded: boolean;
  /**
   * One.com's WSB subscription page, taken as-is from `WSB_UPGRADE_URL` (see
   * environments.js). No locale path and no query parameters: the merchant is
   * already signed in to One.com — app-shell runs inside its iframe — so the
   * page resolves their project and plan itself. `null` on environments that
   * configure none.
   */
  upgradeUrl: string | null;
}

/** The merchant fields the decision depends on. */
export interface EligibilityInput {
  /** `null` for merchants who are not on WSB. */
  wsbPlan: WsbPlan | null;
  /** `null` for merchants with no allowance to spend. */
  appointmentUsage: AppointmentUsage | null;
}

/**
 * Resolves the upsell for a merchant, or `null` when it does not apply.
 *
 * `null` is the "nothing to sell" case: non-WSB merchants, Commerce merchants
 * who are already at the top, the beta toggle being off, and Bookings merchants
 * whose allowance could not be resolved. Core fails open in that last situation
 * too — where it would not block, we do not warn.
 *
 * @param merchant - Merchant fields from the `currentMerchant` slice.
 * @param isBetaEnabled - Whether the `/beta` toggle is on. Passed in rather than
 *   read here so React callers can supply a value from `useLocalStorageState`
 *   and re-render when the switch flips.
 */
export function resolveUpgradeEligibility(
  merchant: EligibilityInput,
  isBetaEnabled: boolean
): UpgradeEligibility | null {
  if (!isBetaEnabled) return null;

  const { wsbPlan, appointmentUsage: usage } = merchant;

  if (!wsbPlan) return null;

  const upgradeTarget = upgradeTargetFor(wsbPlan);
  if (!upgradeTarget) return null;

  const upgradeUrl = WSB_UPGRADE_URL || null;

  // Portfolio quotes no numbers, so it needs none: the zeros stand for "no
  // allowance", and `bookingAllowed: false` is what tells every downstream
  // classifier this merchant is blocked rather than merely under the line.
  if (wsbPlan === 'portfolio') {
    return {
      plan: wsbPlan,
      upgradeTarget,
      appointmentsIncluded: false,
      used: 0,
      limit: 0,
      bookingAllowed: false,
      upgradeUrl,
    };
  }

  if (!usage || usage.limit <= 0) return null;

  return {
    ...usage,
    plan: wsbPlan,
    upgradeTarget,
    appointmentsIncluded: true,
    upgradeUrl,
  };
}
